'use client';

import type { MapData, MapIconFonts } from '@repo/types/maps/map-types';
import { create as createFont, type Font } from 'fontkit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getFontRequirementState } from './font-requirements';
import { BUILT_IN_FONT_NAMES, getBuiltInFontUrls, getFontStyleUrls, getMapFontUrls } from './fontUtils';
let fontsCache: MapIconFonts | null = null;
let fontsPromise: Promise<MapIconFonts> | null = null;
const fullFontsCache: MapIconFonts = {};
const fullFontsPromises: Partial<Record<string, Promise<Font>>> = {};
const fontStyleUrls = new Map<string, string>();
const FONT_RETRY_INITIAL_DELAY_MS = 1_000;
const FONT_RETRY_MAX_DELAY_MS = 30_000;
const createEmptyFonts = (): MapIconFonts => Object.fromEntries(BUILT_IN_FONT_NAMES.map(fontName => [fontName, null]));
const GENERIC_FONTS = BUILT_IN_FONT_NAMES;
async function fetchFontBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${response.status}`);
  }
  return response.arrayBuffer();
}
const loadFonts = async (): Promise<MapIconFonts> => {
  if (fontsPromise) {
    return fontsPromise;
  }
  const pendingFonts = (async () => {
    const fonts = {
      ...createEmptyFonts(),
      ...(await loadFullFonts(GENERIC_FONTS))
    };
    injectFontStyles(getBuiltInFontUrls(), true);
    fontsCache = fonts;
    return fonts;
  })();
  fontsPromise = pendingFonts;
  try {
    return await pendingFonts;
  } catch (error) {
    if (fontsPromise === pendingFonts) {
      fontsPromise = null;
    }
    throw error;
  }
};

/**
 * Load a specific full font from API
 */
async function loadFullFont(fontName: string): Promise<Font> {
  if (fullFontsCache[fontName]) {
    return fullFontsCache[fontName];
  }
  if (fullFontsPromises[fontName]) {
    return fullFontsPromises[fontName];
  }
  const pendingFont = (async () => {
    const fontSources = getBuiltInFontUrls();
    const fontSource = fontSources[fontName];
    if (!fontSource) throw new Error(`Unknown built-in font: ${fontName}`);
    const buffer = await fetchFontBuffer(fontSource);
    const font = createFont(Buffer.from(buffer)) as Font;
    fullFontsCache[fontName] = font;
    return font;
  })();
  fullFontsPromises[fontName] = pendingFont;
  try {
    return await pendingFont;
  } catch (error) {
    if (fullFontsPromises[fontName] === pendingFont) {
      delete fullFontsPromises[fontName];
    }
    throw error;
  }
}

/**
 * Load specific full fonts from API
 */
async function loadFullFonts(fontNames: string[]): Promise<MapIconFonts> {
  const fonts: MapIconFonts = {};
  await Promise.all(fontNames.map(async fontName => {
    try {
      fonts[fontName] = await loadFullFont(fontName);
    } catch {
      fonts[fontName] = null;
    }
  }));
  return fonts;
}
function injectFontStyles(fontUrls: Record<string, string>, replace = false): void {
  if (replace) {
    fontStyleUrls.clear();
  }
  for (const [fontName, url] of Object.entries(fontUrls)) {
    fontStyleUrls.set(fontName, url);
  }
  let styleElement = document.getElementById('map-icon-fonts');
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'map-icon-fonts';
    document.head.appendChild(styleElement);
  }
  styleElement.textContent = [...fontStyleUrls].map(([fontName, url]) => `@font-face {
                font-family: ${JSON.stringify(fontName)};
                src: url(${JSON.stringify(url)}) format('truetype');
                font-display: block;
            }`).join('\n');
}
const loadSubsetFonts = async (fontUrls: Record<string, string>): Promise<MapIconFonts> => {
  const fontPromises: Promise<[string, Font] | null>[] = [];
  for (const [fontName, url] of Object.entries(fontUrls)) {
    if (url) {
      fontPromises.push(fetchFontBuffer(url).then(buffer => [fontName, createFont(Buffer.from(buffer)) as Font] as [string, Font]).catch(() => null));
    }
  }
  const loadedFonts = await Promise.all(fontPromises);
  const fonts: MapIconFonts = {};
  for (const loadedFont of loadedFonts) {
    if (!loadedFont) continue;
    const [fontName, font] = loadedFont;
    fonts[fontName] = font;
  }
  return fonts;
};
export function useFontLoader(mapData?: MapData | null, options?: {
  loadFullFonts?: boolean;
}) {
  const [fonts, setFonts] = useState<MapIconFonts | null>(fontsCache);
  const [isLoading, setIsLoading] = useState(!fontsCache);
  const currentMapDataRef = useRef<MapData | null>(null);
  const loadingRef = useRef(false);
  const pendingMapDataRef = useRef<MapData | null>(null);
  const loadedRequirementsRef = useRef<string | null>(null);
  const [readyRequirementsKey, setReadyRequirementsKey] = useState<string | null>(null);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadFullFontsRef = useRef(Boolean(options?.loadFullFonts));
  loadFullFontsRef.current = Boolean(options?.loadFullFonts);
  const loadFontsForMapData = useCallback(async (data: MapData | null) => {
    if (loadingRef.current) {
      pendingMapDataRef.current = data;
      return;
    }
    loadingRef.current = true;
    setIsLoading(true);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const fontUrls = getMapFontUrls(data);
    const requirementsKey = Object.values(fontUrls).sort().join('|');
    if (requirementsKey === loadedRequirementsRef.current) {
      setIsLoading(false);
      loadingRef.current = false;
      return;
    }
    try {
      const hasRequiredFonts = Object.keys(fontUrls).length > 0;
      if (hasRequiredFonts) {
        const subsetFonts = await loadSubsetFonts(fontUrls);
        const requestedFonts = Object.keys(fontUrls);
        const missingFonts = requestedFonts.filter(fontName => BUILT_IN_FONT_NAMES.includes(fontName) && !subsetFonts[fontName]);
        const fullFonts_0 = await loadFullFonts(missingFonts);
        const loadedFonts = {
          ...createEmptyFonts(),
          ...fullFonts_0,
          ...subsetFonts
        };
        const unavailableFonts = requestedFonts.filter(fontName_0 => !loadedFonts[fontName_0]);
        if (unavailableFonts.length > 0) {
          throw new Error(`Failed to load required fonts: ${unavailableFonts.join(', ')}`);
        }
        const builtInFontUrls = getBuiltInFontUrls();
        const fallbackFontUrls: Record<string, string> = {};
        for (const fontName_1 of missingFonts) {
          const url = builtInFontUrls[fontName_1];
          if (url) fallbackFontUrls[fontName_1] = url;
        }
        const fontStyleSources = {
          ...fontUrls,
          ...fallbackFontUrls
        };
        injectFontStyles(getFontStyleUrls(fontStyleSources, loadFullFontsRef.current), true);
        loadedRequirementsRef.current = requirementsKey;
        setReadyRequirementsKey(requirementsKey);
        retryAttemptRef.current = 0;
        fontsCache = loadedFonts;
        setFonts(loadedFonts);
      } else {
        const fullFonts_1 = await loadFonts();
        loadedRequirementsRef.current = requirementsKey;
        setReadyRequirementsKey(requirementsKey);
        retryAttemptRef.current = 0;
        setFonts(fullFonts_1);
      }
    } catch {
      loadedRequirementsRef.current = null;
      const fullFonts = await loadFonts();
      setReadyRequirementsKey(requirementsKey);
      setFonts(fullFonts);
      if (currentMapDataRef.current === data) {
        const retryDelay = Math.min(FONT_RETRY_INITIAL_DELAY_MS * 2 ** retryAttemptRef.current, FONT_RETRY_MAX_DELAY_MS);
        retryAttemptRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          if (currentMapDataRef.current === data) {
            void loadFontsForMapData(data);
          }
        }, retryDelay);
      }
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
      const pendingMapData = pendingMapDataRef.current;
      pendingMapDataRef.current = null;
      if (pendingMapData && pendingMapData !== data) {
        void loadFontsForMapData(pendingMapData);
      }
    }
  }, []);
  useEffect(() => {
    if (mapData && currentMapDataRef.current !== mapData) {
      currentMapDataRef.current = mapData;
      retryAttemptRef.current = 0;
      void loadFontsForMapData(mapData);
    } else if (!mapData && !fontsCache) {
      void loadFonts().then(loadedFonts_0 => {
        if (currentMapDataRef.current) {
          return;
        }
        setFonts(loadedFonts_0);
        setIsLoading(false);
      });
    }
  }, [mapData, loadFontsForMapData]);
  useEffect(() => () => {
    currentMapDataRef.current = null;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Load all fonts fully when creator mode is enabled
  useEffect(() => {
    if (!options?.loadFullFonts) {
      return;
    }
    if (GENERIC_FONTS.every(fontName_2 => fullFontsCache[fontName_2])) {
      injectFontStyles(getBuiltInFontUrls());
      setFonts(currentFonts => ({
        ...createEmptyFonts(),
        ...currentFonts,
        ...fullFontsCache
      }));
      return;
    }
    setIsLoading(true);
    const allFonts = GENERIC_FONTS;
    loadFullFonts(allFonts).then(fullFonts_2 => {
      if (!loadFullFontsRef.current) {
        setIsLoading(false);
        return;
      }
      injectFontStyles(getBuiltInFontUrls());
      setFonts(currentFonts_0 => ({
        ...createEmptyFonts(),
        ...currentFonts_0,
        ...fullFonts_2
      }));
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [options?.loadFullFonts]);
  const refetchFonts = useCallback(() => {
    if (mapData) {
      retryAttemptRef.current = 0;
      void loadFontsForMapData(mapData);
    }
  }, [mapData, loadFontsForMapData]);
  const requiredFontsKey = Object.values(getMapFontUrls(mapData ?? null)).sort().join('|');
  const requirementState = getFontRequirementState(requiredFontsKey, loadedRequirementsRef.current, readyRequirementsKey);
  const readyFonts = requirementState.isReady ? fonts : null;
  return {
    fonts: readyFonts,
    isLoading,
    refetchFonts
  };
}